import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { usePostHog as usePostHogHook } from "posthog-js/react";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";

type TrackPropertyValue = string | number | boolean | string[] | number[];
type TrackProperties = Partial<Record<string, TrackPropertyValue>>;

type WidgetTrackingIdentity = {
  appContext: string;
  organizationId?: string;
  widgetId?: string;
  /** Additional static properties merged into every event (e.g. bookerId). */
  extra?: TrackProperties;
};

type WidgetTrackingContextValue = {
  track: (event: PostHogCustomEvent, properties?: TrackProperties) => void;
};

const WidgetTrackingContext =
  createContext<WidgetTrackingContextValue | null>(null);

export const WidgetTrackingProvider = ({
  appContext,
  organizationId,
  widgetId,
  extra,
  children,
}: PropsWithChildren<WidgetTrackingIdentity>) => {
  const posthog = usePostHogHook();

  const track = useCallback(
    (event: PostHogCustomEvent, properties?: TrackProperties) => {
      posthog.capture(event, {
        appContext,
        ...(organizationId ? { organizationId } : {}),
        ...(widgetId ? { widgetId } : {}),
        ...extra,
        ...properties,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posthog, appContext, organizationId, widgetId, JSON.stringify(extra)],
  );

  const value = useMemo<WidgetTrackingContextValue>(
    () => ({ track }),
    [track],
  );

  return (
    <WidgetTrackingContext.Provider value={value}>
      {children}
    </WidgetTrackingContext.Provider>
  );
};

/**
 * Returns a `track(event, props)` function that merges this widget's identity
 * into every event. Falls back to bare posthog.capture when used outside a
 * WidgetTrackingProvider (e.g. in non-widget contexts that share the same
 * shared components).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useWidgetTrack = () => {
  const context = useContext(WidgetTrackingContext);
  if (context) return context.track;
  return useFallbackTrack();
};

function useFallbackTrack() {
  const posthog = usePostHogHook();
  return useCallback(
    (event: PostHogCustomEvent, properties?: TrackProperties) => {
      posthog.capture(event, properties);
    },
    [posthog],
  );
}
